import { useEffect, useMemo, useState } from 'react';

import ModeMoonIcon from '@/assets/all-icons/mode-moon-icon';
import ModeSunIcon from '@/assets/all-icons/mode-sun-icon';

import './ToggleModeDarkLight.css';

/**
 * Función auxiliar para animar el cambio de tema
 * usando un círculo que se expande desde top-right.
 */
function startThemeTransitionCircleBlur(updateFn) {
  // Respeta preferencias de accesibilidad
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    updateFn();
    return;
  }

  const supportsVT = 'startViewTransition' in document;
  const styleId = `theme-transition-${Date.now()}`;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @supports (view-transition-name: root) {
      ::view-transition-old(root) { animation: none; }
      ::view-transition-new(root) {
        animation: circle-blur-expand 500ms ease-out;
        transform-origin: top right;
      }
      @keyframes circle-blur-expand {
        from { clip-path: circle(0% at 100% 0%); filter: blur(4px); }
        to   { clip-path: circle(150% at 100% 0%); filter: blur(0); }
      }
    }
  `;
  document.head.appendChild(style);

  const cleanup = () => {
    const el = document.getElementById(styleId);
    if (el) el.remove();
  };

  const doUpdate = () => {
    try {
      updateFn();
    } finally {
      setTimeout(cleanup, 800); // limpiar tras la animación
    }
  };

  if (supportsVT) {
    // @ts-ignore
    document.startViewTransition(doUpdate);
  } else {
    doUpdate();
  }
}

/**
 * Toggle Dark/Light con iconos (sol/luna) compatible con mode.styles.css:
 * - DARK = estado por defecto -> sin atributo data-mode (usa :root)
 * - LIGHT = añade data-mode="light" en <html>
 */
export default function ToggleModeDarkLight({ className = '' }) {
  const STORAGE_KEY = 'suarte.themeMode';
  const LIGHT = 'light';
  const DARK = 'dark';

  // Estado inicial: DOM -> localStorage -> prefers-color-scheme
  const initialIsDark = useMemo(() => {
    try {
      const domAttr = document.documentElement.getAttribute('data-mode');
      if (domAttr === LIGHT) return false;
      if (domAttr === DARK) return true;

      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === DARK) return true;
      if (saved === LIGHT) return false;
    } catch {
      // Intentionally ignore errors
    }
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }, []);

  const [isDark, setIsDark] = useState(initialIsDark);

  // Aplica al <html> (oscuro = sin data-mode, claro = data-mode="light")
  useEffect(() => {
    try {
      if (isDark) {
        document.documentElement.removeAttribute('data-mode');
        localStorage.setItem(STORAGE_KEY, DARK);
      } else {
        document.documentElement.setAttribute('data-mode', LIGHT);
        localStorage.setItem(STORAGE_KEY, LIGHT);
      }
    } catch {
      // Intentionally ignore errors
    }
  }, [isDark]);

  const aria = isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro';

  const handleClick = () => {
    startThemeTransitionCircleBlur(() => setIsDark(v => !v));
  };

  return (
    <button
      type="button"
      className={`mode-toggle ${className}`}
      aria-label={aria}
      onClick={handleClick}
    >
      {/* 👇 checked SOLO en LIGHT para que el knob vaya a la derecha en claro */}
      <span className={`mode-track ${!isDark ? 'checked' : ''}`}>
        {/* Iconos fijos: izq = luna, dcha = sol */}
        <span className="mode-icon mode-icon--left" aria-hidden="true">
          <ModeMoonIcon />
        </span>
        <span className="mode-icon mode-icon--right" aria-hidden="true">
          <ModeSunIcon />
        </span>

        {/* Knob: oscuro = luna, claro = sol */}
        <span className="mode-knob" aria-hidden="true">
          {isDark ? (
            <ModeMoonIcon className="mode-knob__icon" />
          ) : (
            <ModeSunIcon className="mode-knob__icon" />
          )}
        </span>
      </span>
    </button>
  );
}
